import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { yeastarAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Phone, Plug, CheckCircle2, Loader2, Webhook, Users, RefreshCw, Copy, Download, KeyRound, Activity, AlertTriangle, XCircle, Stethoscope } from 'lucide-react';

const CopyRow = ({ label, value, testid }) => {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Copied');
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = value;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        ta.remove();
        toast.success('Copied');
      } catch {
        toast.error('Copy failed — select and copy manually');
      }
    }
  };
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <div className="flex items-center gap-2">
        <code className="flex-1 text-xs bg-slate-50 border border-slate-200 rounded-md px-2 py-1.5 break-all text-slate-700" data-testid={testid}>{value}</code>
        <Button variant="outline" size="sm" className="shrink-0" onClick={copy} data-testid={`${testid}-copy`}>
          <Copy className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
};

export const YeastarSettings = () => {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ edition: 'cloud', base_url: '', client_id: '', client_secret: '', verify_tls: true, webhook_secret: '' });
  const [userMap, setUserMap] = useState([]);
  const [regenerating, setRegenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [extensions, setExtensions] = useState([]);
  const [egressIp, setEgressIp] = useState('');
  const [diag, setDiag] = useState(null);
  const [diagnosing, setDiagnosing] = useState(false);

  const loadConfig = () => {
    yeastarAPI.getConfig().then((r) => {
      setCfg(r.data);
      setForm((f) => ({ ...f, edition: r.data.edition || 'cloud', base_url: r.data.base_url || '', client_id: r.data.client_id || '', verify_tls: r.data.verify_tls !== false }));
    }).catch(() => {});
    yeastarAPI.egressIp().then((r) => setEgressIp(r.data.ip || '')).catch(() => {});
  };
  const loadMap = () => yeastarAPI.getExtensionMap().then((r) => setUserMap(r.data || [])).catch(() => {});

  useEffect(() => { loadConfig(); loadMap(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await yeastarAPI.saveConfig(form);
      toast.success('Yeastar API configuration saved');
      setForm((f) => ({ ...f, client_secret: '', webhook_secret: '' }));
      loadConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await yeastarAPI.test();
      if (r.data.ok) toast.success(`Connected — ${r.data.total_extensions ?? '?'} extensions found`);
      else toast.error(r.data.errmsg || 'Connection failed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Connection failed');
    } finally { setTesting(false); }
  };

  const loadExtensions = async () => {
    try {
      const r = await yeastarAPI.getExtensions();
      setExtensions(r.data || []);
      toast.success(`${(r.data || []).length} extensions loaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load extensions');
    }
  };

  const regenerate = async () => {
    if (!window.confirm('Generate a new API key? Your PBX template will stop working until you re-import it.')) return;
    setRegenerating(true);
    try {
      await yeastarAPI.regenerateKey();
      toast.success('New API key generated — re-download & import the template');
      loadConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to regenerate key');
    } finally { setRegenerating(false); }
  };

  const downloadTemplate = async () => {
    try {
      const r = await yeastarAPI.xmlTemplate();
      const blob = new Blob([r.data], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'helios-crm-yeastar-template.xml';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch { toast.error('Could not download template'); }
  };

  const setExt = async (userId, ext) => {
    try {
      await yeastarAPI.setExtension(userId, ext);
      setUserMap((m) => m.map((u) => (u.id === userId ? { ...u, yeastar_extension: ext } : u)));
    } catch { toast.error('Failed to save extension'); }
  };

  const runSelfTest = async () => {
    setDiagnosing(true);
    try {
      const r = await yeastarAPI.selfTest();
      setDiag(r.data);
      toast.success('Connectivity check complete');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Self-test failed');
    } finally { setDiagnosing(false); }
  };

  const ep = cfg?.endpoints || {};

  const STATUS_META = {
    ok: { cls: 'bg-green-50 border-green-200 text-green-800', Icon: CheckCircle2, iconCls: 'text-green-600', label: 'Working' },
    warn: { cls: 'bg-amber-50 border-amber-200 text-amber-800', Icon: AlertTriangle, iconCls: 'text-amber-600', label: 'Attention' },
    fail: { cls: 'bg-rose-50 border-rose-200 text-rose-800', Icon: XCircle, iconCls: 'text-rose-600', label: 'Not working' },
    not_configured: { cls: 'bg-slate-50 border-slate-200 text-slate-600', Icon: Plug, iconCls: 'text-slate-400', label: 'Optional' },
  };
  const Pillar = ({ title, node, testid }) => {
    const meta = STATUS_META[node?.status] || STATUS_META.warn;
    const { Icon } = meta;
    return (
      <div className={`rounded-lg border p-3 ${meta.cls}`} data-testid={testid}>
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 flex-shrink-0 ${meta.iconCls}`} />
          <p className="text-sm font-semibold flex-1">{title}</p>
          <Badge variant="outline" className="text-[10px] bg-white/60">{meta.label}</Badge>
        </div>
        <p className="text-xs mt-1.5 leading-relaxed">{node?.message}</p>
      </div>
    );
  };
  const fmtWhen = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('en-GB'); } catch { return iso; }
  };

  return (
    <div className="space-y-6">
      {/* One-click connectivity self-test */}
      <Card className="border-indigo-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Stethoscope className="w-5 h-5 text-indigo-600" /> Yeastar connectivity self-test
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-500">Checks all three parts of the integration and tells you exactly why calls may not be flowing — no server logs needed. Make a test call first, then run this.</p>
          <Button onClick={runSelfTest} disabled={diagnosing} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="yeastar-selftest-run">
            {diagnosing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Activity className="w-4 h-4 mr-2" />} Run connectivity check
          </Button>

          {diag && (
            <div className="space-y-3" data-testid="yeastar-selftest-result">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Pillar title="1. Call logging & contact pop (PBX → CRM)" node={diag.xml} testid="selftest-xml" />
                <Pillar title="2. Advanced features (CRM → PBX)" node={diag.openapi} testid="selftest-openapi" />
                <Pillar title="3. Live pop-up webhook (PBX → CRM)" node={diag.webhook} testid="selftest-webhook" />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <div className="rounded-md bg-slate-50 border p-2"><p className="text-slate-400">PBX hits (24h)</p><p className="font-semibold text-slate-700">{diag.xml?.hits_24h ?? 0} <span className="text-slate-400 font-normal">/ {diag.xml?.hits_total ?? 0} total</span></p></div>
                <div className="rounded-md bg-slate-50 border p-2"><p className="text-slate-400">Auth OK / rejected (24h)</p><p className="font-semibold text-slate-700">{diag.xml?.auth_ok_24h ?? 0} / {diag.xml?.auth_fail_24h ?? 0}</p></div>
                <div className="rounded-md bg-slate-50 border p-2"><p className="text-slate-400">Call logs (24h)</p><p className="font-semibold text-slate-700">{diag.call_logs?.last_24h ?? 0} <span className="text-slate-400 font-normal">/ {diag.call_logs?.total ?? 0} total</span></p></div>
                <div className="rounded-md bg-slate-50 border p-2"><p className="text-slate-400">Last PBX hit</p><p className="font-semibold text-slate-700">{fmtWhen(diag.xml?.last_hit?.created_at)}</p></div>
              </div>

              <div className="text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
                <span>CRM URL the PBX must reach: <code className="text-slate-700">{diag.crm_base_url}</code></span>
                <span>API key configured: <span className={diag.api_key_set ? 'text-green-600' : 'text-rose-600'}>{diag.api_key_set ? 'yes' : 'no'}</span></span>
                {diag.egress_ip && <span>CRM egress IP: <code className="text-slate-700">{diag.egress_ip}/32</code></span>}
              </div>

              {(diag.recent_hits || []).length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <p className="text-xs font-medium text-slate-600 px-3 py-2 bg-slate-50 border-b">Recent PBX → CRM attempts</p>
                  <div className="divide-y max-h-48 overflow-y-auto" data-testid="selftest-recent-hits">
                    {diag.recent_hits.map((h, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                        {h.auth_ok ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-rose-500 flex-shrink-0" />}
                        <span className="font-mono text-slate-500 w-36 flex-shrink-0">{fmtWhen(h.created_at)}</span>
                        <span className="font-mono text-slate-700 flex-1 truncate">{h.method} {h.path}</span>
                        {h.reason && <span className="text-rose-600 truncate">{h.reason}</span>}
                        {h.ip && <span className="text-slate-400 flex-shrink-0">{h.ip}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* XML — call logging + contact pop */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Phone className="w-5 h-5 text-green-600" /> Call logging &amp; contact pop (Custom CRM Integration)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-500">Your PBX pushes call logs &amp; contact screen-pops to the CRM — no firewall changes needed. Download the pre-filled template and import it on the Yeastar portal (<span className="font-medium">Integrations → CRM → Custom → Template Management → Add / Import</span>).</p>
          <Button onClick={downloadTemplate} className="bg-green-600 hover:bg-green-700 text-white" data-testid="yeastar-download-template">
            <Download className="w-4 h-4 mr-2" /> Download PBX template (pre-filled)
          </Button>
          {cfg && (
            <div className="space-y-3 pt-1" data-testid="yeastar-how-to">
              <CopyRow label="API Key" value={cfg.api_key || ''} testid="yeastar-api-key" />
              <CopyRow label="Contact Lookup URL" value={ep.contacts || ''} testid="yeastar-ep-contacts" />
              <CopyRow label="Call Journal URL (POST)" value={ep.call_journal || ''} testid="yeastar-ep-journal" />
              <CopyRow label="User Association URL" value={ep.users || ''} testid="yeastar-ep-users" />
              <Button onClick={regenerate} disabled={regenerating} variant="outline" size="sm" className="text-amber-700 border-amber-300 hover:bg-amber-50" data-testid="yeastar-regenerate-key">
                {regenerating ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-2" />} Regenerate API key
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* OpenAPI — advanced features */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <KeyRound className="w-5 h-5 text-sky-600" /> Advanced features (recordings, click-to-call, live controls)
            {cfg?.api_configured && <Badge className="bg-green-100 text-green-700 border-green-200 ml-2">Connected</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-500">Enter the PBX API credentials so the CRM can play call recordings, pause/resume live, place click-to-call and pop incoming calls. These need the CRM's IP allow-listed on the PBX API.</p>
          {egressIp && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-indigo-50 border border-indigo-200 text-sm" data-testid="yeastar-egress-ip">
              <Plug className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-slate-700">Allow-list this CRM's outbound IP on your PBX API settings: </span>
                <code className="font-semibold text-indigo-700">{egressIp}/32</code>
                <p className="text-xs text-slate-500 mt-0.5">Production uses a different egress IP than preview — read this banner on the deployed app too if the PBX shows "IP FORBIDDEN".</p>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Edition</Label>
              <select className="w-full mt-1 border rounded-md h-10 px-3 text-sm" value={form.edition} onChange={(e) => setForm({ ...form, edition: e.target.value })} data-testid="yeastar-edition">
                <option value="cloud">P-Series Cloud Edition</option>
                <option value="software">P-Series Software / Appliance Edition</option>
              </select>
            </div>
            <div>
              <Label>PBX API Base URL</Label>
              <Input className="mt-1" placeholder={form.edition === 'cloud' ? 'https://yourcompany.ras.yeastar.com' : 'https://pbx.yourdomain.com:8088'} value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} data-testid="yeastar-base-url" />
            </div>
            <div>
              <Label>Client ID</Label>
              <Input className="mt-1" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="yeastar-client-id" />
            </div>
            <div>
              <Label>Client Secret {cfg?.client_secret_set && <span className="text-xs text-green-600">(saved — leave blank to keep)</span>}</Label>
              <Input className="mt-1" type="password" placeholder={cfg?.client_secret_set ? '••••••••' : ''} value={form.client_secret} onChange={(e) => setForm({ ...form, client_secret: e.target.value })} data-testid="yeastar-client-secret" />
            </div>
          </div>
          {form.edition === 'software' && (
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input type="checkbox" checked={form.verify_tls === false} onChange={(e) => setForm({ ...form, verify_tls: !e.target.checked })} data-testid="yeastar-skip-tls" />
              Skip TLS certificate verification (tick if your self-hosted PBX uses a self-signed certificate)
            </label>
          )}
          <div className="flex flex-wrap gap-2">
            <Button onClick={save} disabled={saving} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="yeastar-save">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plug className="w-4 h-4 mr-2" />} Save
            </Button>
            <Button onClick={test} disabled={testing || !cfg?.api_configured} variant="outline" data-testid="yeastar-test">
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Test connection
            </Button>
          </div>
          <div className="pt-2 border-t space-y-3">
            <p className="text-sm font-medium text-slate-700 flex items-center gap-2"><Webhook className="w-4 h-4 text-sky-600" /> Webhook Event Push (this is how calls get logged)</p>
            <p className="text-xs text-slate-500">On the Yeastar portal: <span className="font-medium">Integrations → API → Webhook Event Push</span>. Set the Webhook URL below, subscribe to events <span className="font-medium">30011 &amp; 30012</span>, and paste the same signing secret here so the CRM can verify it.</p>
            <CopyRow label="Webhook URL" value={`${cfg?.crm_base_url || ''}/api/yeastar/webhook`} testid="yeastar-webhook-url" />
            <div>
              <Label>Webhook signing secret {cfg?.webhook_secret_set && <span className="text-xs text-green-600">(saved — leave blank to keep)</span>}</Label>
              <Input className="mt-1" type="password" placeholder={cfg?.webhook_secret_set ? '••••••••' : 'Paste the Secret shown in Yeastar'} value={form.webhook_secret} onChange={(e) => setForm({ ...form, webhook_secret: e.target.value })} data-testid="yeastar-webhook-secret" />
              <p className="text-[11px] text-slate-400 mt-1">Optional but recommended. Calls still log without it; matching the secret just lets the CRM verify each webhook is genuinely from your PBX.</p>
            </div>
            <p className="text-xs text-slate-400">Tip: watch the "PBX connection activity" panel on the Call Analytics page to confirm webhooks are arriving.</p>
          </div>
        </CardContent>
      </Card>

      {/* Extension mapping */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Users className="w-4 h-4 text-indigo-600" /> Map extensions to staff</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-500">Tie each staff member's Yeastar extension to their CRM account so incoming-call pop-ups, live recording controls and per-staff analytics reach the right person.</p>
          <Button onClick={loadExtensions} disabled={!cfg?.api_configured} variant="outline" size="sm" data-testid="yeastar-load-extensions">
            <RefreshCw className="w-3.5 h-3.5 mr-2" /> Load PBX extensions
          </Button>
          {extensions.length > 0 && (
            <p className="text-xs text-slate-500">Available: {extensions.map((e) => `${e.number}${e.name ? ` (${e.name})` : ''}`).join(', ')}</p>
          )}
          <div className="divide-y border rounded-lg">
            {userMap.map((u) => (
              <div key={u.id} className="flex items-center justify-between gap-3 px-3 py-2" data-testid={`ext-row-${u.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{u.name}</p>
                  <p className="text-xs text-slate-400 truncate">{u.email}</p>
                </div>
                <Input className="w-28 h-8 text-sm" placeholder="ext." defaultValue={u.yeastar_extension || ''} onBlur={(e) => setExt(u.id, e.target.value.trim())} data-testid={`ext-input-${u.id}`} />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default YeastarSettings;
